import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Grid, MenuItem, Stack, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import CustomSelect from "../../../components/customSelect";
import AppointmentAssigningDialog from "../../../components/Dialogs/AppointmentAssigningDIalog";
import Label from "../../../components/Label";
import NavigationDrawer from "../../../components/NavigationDrawer";
import TextInput from "../../../components/TextInputs";
import { getAppointmentDetail } from "../../../services/appointmentApiFn";
import { getCompanyBySubServices } from "../../../services/comanyApiFn";
import { convertMinutesToHours, handleOpenSnackBar } from "../../../utils";
import AppLoader from "./../../../components/AppLoader/index";
import ClientAnswer from "./ClientAnswer";
import CompanyDetail from "./ComanyDetail";
import RejectedDetail from "./RejectedDetail";
import ServiceDetail from "./ServiceDetail";
import UserDetail from "./UserDetail";
import WorkerDetail from "./WorkerDetail";
import { t } from "i18next";

const WORKER_STATUS = [
  {
    name: "pending",
    value: "pending",
  },
  {
    name: "Accepted",
    value: "accepted",
  },
  {
    name: "Rejected",
    value: "rejected",
  },
];
const APPOINTMENT_STATUS = [
  {
    name: "Pending",
    value: "pending",
  },
  {
    name: "Arrived",
    value: "accepted",
  },
  {
    name: "Started",
    value: "started",
  },
  {
    name: "Completed",
    value: "completed",
  },
];

function AppointmentDetailPage() {
  const openAppointmentDialog = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [isLoading, setIsLoading] = useState();
  const [appointmentDetail, setAppointmentDetail] = useState(null);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [inputValues, setInputValues] = useState({
    assignedCompany: "",
    assignedWorker: "",
    workerStatus: "",
    appointmentStatus: "",
  });
  const { currentLanguage } = useSelector((state) => state.languageReducer);

  useEffect(() => {
    if (params?.id) {
      fetchAppointmentDetail();
    }
  }, [params?.id]);

  const fetchAppointmentDetail = async () => {
    try {
      setIsLoading(true);
      const response = await getAppointmentDetail(params?.id);
      if (response?.data.status === "success") {
        const firstData = response.data.data;
        setAppointmentDetail(firstData);
        setInputValues((prev) => ({
          ...prev,
          assignedCompany: firstData?.companyId?._id,
          assignedWorker: firstData?.workerId?._id,
          workerStatus: firstData?.workerStatus,
          appointmentStatus: firstData?.status,
        }));

        const resForCom = await getCompanyBySubServices({
          subServices: [firstData?.serviceSubCategoryId?._id],
          companyId: firstData.companyId?._id,
          price: firstData.companyServiceDetails.price,
          selectedType: firstData?.userInputs?.selectedOption,
        });

        if (resForCom?.data.status === "success") {
          setAvailableCompanies(resForCom?.data?.data || []);
        }
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeaderAction = () => {
    return (
      <Stack flexGrow={1} direction={{ md: "row" }} justifyContent={"flex-end"}>
        <GlobalButton
          variant={"contained"}
          btnText={"Assigned"}
          startIcon={<AssignmentIcon />}
          handleClickBtn={() => {
            let isExpired = new Date(appointmentDetail?.start) > new Date();
            if (!isExpired) {
              handleOpenSnackBar("error", t("appointmentTimeOver"));
            } else {
              openAppointmentDialog.current.openDialog({
                data: appointmentDetail,
                getData: fetchAppointmentDetail,
              });
            }
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />
      </Stack>
    );
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("appointmentDetail")}
        subtitle={t("viewAllAppointmentDetails")}
        showBackButton={true}
        onBack={() => navigate("/appointments")}
        headerActions={
          appointmentDetail?.workerStatus === "rejected" && renderHeaderAction()
        }
      >
        <Typography variant="h6" fontWeight={500} my={2}>
          {t("appointmentDetail")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("createdAt")} />
            <TextInput
              value={moment(appointmentDetail?.createdAt).format("MM/DD/YYYY")}
              readonly
              InputEndIcon={<CalendarMonthIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("appointmentId")} />
            <TextInput value={appointmentDetail?.appointmentId} readonly />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("totalCost")} />
            <TextInput
              value={appointmentDetail?.totalCost}
              readonly
              InputEndIcon={"RON"}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("totalDuration")} />
            <TextInput
              value={convertMinutesToHours(appointmentDetail?.duration)}
              readonly
              InputEndIcon={<AccessTimeIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("startDateTime")} />
            <TextInput
              value={moment(appointmentDetail?.start).format(
                "MM/DD/YYYY [at] hh:mm:a"
              )}
              readonly
              InputEndIcon={<AccessTimeIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("endDateTime")} />
            <TextInput
              value={moment(appointmentDetail?.end).format(
                "MM/DD/YYYY [at] hh:mm:a"
              )}
              readonly
              InputEndIcon={<AccessTimeIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("adminPercentage")} />
            <TextInput
              value={appointmentDetail?.percentage}
              readonly
              InputEndIcon={"%"}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("companyPercentage")} />
            <TextInput
              value={appointmentDetail?.companyPercentage}
              readonly
              InputEndIcon={"%"}
            />
          </Grid>

          {appointmentDetail?.userInputs?.selectedType === "room+area" &&
            appointmentDetail?.userInputs?.selectedOption === "room" && (
              <Grid item xs={12} sm={6} md={3}>
                <Label labelText={t("userInput")} />
                <TextInput
                  value={`${appointmentDetail?.userInputs?.userInput} Rooms`}
                  readonly
                />
              </Grid>
            )}
          {appointmentDetail?.userInputs?.selectedType === "room+area" &&
            appointmentDetail?.userInputs?.selectedOption === "area" && (
              <Grid item xs={12} sm={6} md={3}>
                <Label labelText={t("userInput")} />
                <TextInput
                  value={`${appointmentDetail?.userInputs?.userInput} sqft`}
                  readonly
                />
              </Grid>
            )}

          {appointmentDetail?.userInputs.selectedType === "room+area" &&
            appointmentDetail?.userInputs.selectedOption === "apartment" && (
              <Grid item xs={12} sm={6} md={3}>
                <Label labelText={t("userInput")} />
                <TextInput
                  value={`${appointmentDetail?.userInputs?.userInput} sqft`}
                  readonly
                />
              </Grid>
            )}
          {appointmentDetail?.userInputs.selectedType === "room+area" &&
            appointmentDetail?.userInputs.selectedOption === "deluxe" && (
              <Grid item xs={12} sm={6} md={3}>
                <Label labelText={t("userInput")} />
                <TextInput
                  value={`${appointmentDetail?.userInputs?.userInput} sqft`}
                  readonly
                />
              </Grid>
            )}
          {appointmentDetail?.userInputs.selectedType === "area" &&
            appointmentDetail?.userInputs.selectedOption === "area" && (
              <Grid item xs={12} sm={6} md={3}>
                <Label labelText={t("userInput")} />
                <TextInput
                  value={`${appointmentDetail?.userInputs?.userInput} sqft`}
                  readonly
                />
              </Grid>
            )}
          {appointmentDetail?.userInputs.selectedType === "fixed" &&
            appointmentDetail?.userInputs.selectedOption === "fixed" && (
              <Grid item xs={12} sm={6} md={3}>
                <Label labelText={t("userInput")} />
                <TextInput value={`car`} readonly />
              </Grid>
            )}
          {appointmentDetail?.userInputs.selectedType === "interior+exterior" &&
            appointmentDetail?.userInputs.selectedOption === "interior" && (
              <Grid item xs={12} sm={6} md={3}>
                <Label labelText={t("userInput")} />
                <TextInput value={`car`} readonly />
              </Grid>
            )}
          {appointmentDetail?.userInputs.selectedType === "interior+exterior" &&
            appointmentDetail?.userInputs.selectedOption === "exterior" && (
              <Grid item xs={12} sm={6} md={3}>
                <Label labelText={t("userInput")} />
                <TextInput value={`Exerior Car`} readonly />
              </Grid>
            )}
          {appointmentDetail?.userInputs.selectedType === "interior+exterior" &&
            appointmentDetail?.userInputs.selectedOption === "both" && (
              <Grid item xs={12} sm={6} md={3}>
                <Label labelText={t("userInput")} />
                <TextInput value={`Interior + Exterior Car`} readonly />
              </Grid>
            )}

          {appointmentDetail?.userInputs?.cleaningType && (
            <Grid item xs={12} sm={6} md={3}>
              <Label labelText={t("cleaningType")} />
              <TextInput
                value={appointmentDetail?.userInputs?.cleaningType}
                readonly
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={500} my={2}>
              {t("updateStatus")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("workerStatus")} />
            <CustomSelect
              value={inputValues?.workerStatus}
              name={"workerStatus"}
            >
              <MenuItem value={""}>{t("selectStatus")}</MenuItem>
              {WORKER_STATUS?.map((val, ind) => {
                return (
                  <MenuItem
                    key={ind}
                    value={val.value}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {val.name}
                  </MenuItem>
                );
              })}
            </CustomSelect>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Label labelText={t("appointmentStatus")} />
            <CustomSelect
              value={inputValues?.appointmentStatus}
              name={"appointmentStatus"}
            >
              <MenuItem value={""}>{t("selectStatus")}</MenuItem>
              {APPOINTMENT_STATUS?.map((val, ind) => {
                return (
                  <MenuItem
                    key={ind}
                    value={val.value}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {val.name}
                  </MenuItem>
                );
              })}
            </CustomSelect>
          </Grid>
        </Grid>

        <Typography variant="h6" fontWeight={500} my={2}>
          {t("serviceDetail")}
        </Typography>

        <ServiceDetail
          detail={appointmentDetail?.serviceSubCategoryDetails}
          companyServiceDetail={appointmentDetail?.companyServiceDetails}
        />

        <Typography variant="h6" fontWeight={500} my={2}>
          {t("assignedCompanyDetail")}
        </Typography>
        <CompanyDetail detail={appointmentDetail?.companyDetails} />
        <Typography variant="h6" fontWeight={500} my={2}>
          {t("assignedWorkerDetail")}
        </Typography>
        <WorkerDetail detail={appointmentDetail?.workerDetails} />
        <Typography variant="h6" fontWeight={500} my={2}>
          {t("clientDetails")}
        </Typography>
        <UserDetail
          detail={appointmentDetail?.userId}
          location={appointmentDetail?.location}
        />
        <Typography variant="h6" fontWeight={500} my={2}>
          {t("clientQuestionAnswer")}
        </Typography>
        <ClientAnswer clientAnswers={appointmentDetail?.clientAnswers} />
        {appointmentDetail?.rejections?.length > 0 && (
          <>
            <Typography variant="h6" fontWeight={500} my={2}>
              {t("previouslyAssignedCompaniesWorkers")}
            </Typography>
            <RejectedDetail rejections={appointmentDetail?.rejections} />
          </>
        )}
      </PaperContainer>

      <AppointmentAssigningDialog
        ref={openAppointmentDialog}
        availableCompanies={availableCompanies}
      />
    </NavigationDrawer>
  );
}

export default AppointmentDetailPage;
